import React, { useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";

/* ============================================================
   SL GOLD THEME
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

const CREAM = "#FBF7F2";
const SOFT_CREAM = "#F6EFEA";

const BORDER = "rgba(43,0,0,.12)";
const GOLD_BORDER = "rgba(212,175,55,.30)";
const TEXT_SOFT = "rgba(43,0,0,.62)";
const TEXT_LIGHT = "rgba(43,0,0,.44)";

/* ============================================================
   ICONS
   ============================================================ */

const BagIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8h12l1 13H5L6 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);

const WalletIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 7V5a2 2 0 0 0-2-2H5a3 3 0 0 0 0 6h15v12H5a3 3 0 0 1-3-3V6" />
    <path d="M16 13h4" />
  </svg>
);

const ClockIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const ReceiptIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" />
    <path d="M9 7h6M9 11h6M9 15h4" />
  </svg>
);

const ArrowIcon = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

/* ============================================================
   DASHBOARD
   ============================================================ */

const DashboardSection = () => {
  const [data, setData] = useState({
    totalOrders: 0,
    totalSpend: 0,
    pendingOrders: 0,
    recentOrders: [],
  });

  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const pageSize = 5;

  const navigate = useNavigate();

  const containerRef = useRef(null);
  const welcomeRef = useRef(null);
  const statsRef = useRef(null);
  const tableRef = useRef(null);

  /* ============================================================
     AUTH
     ============================================================ */

  const clearAuthState = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("user_id");
    localStorage.removeItem("phone");
    localStorage.removeItem("cart_id");
    localStorage.removeItem("userData");

    window.dispatchEvent(
      new Event("authChanged")
    );

    navigate("/login");
  };

  /* ============================================================
     STATS
     ============================================================ */

  const stats = useMemo(
    () => [
      {
        label: "Total Orders",
        value: data.totalOrders,
        caption: "Orders placed",
        icon: <BagIcon />,
      },
      {
        label: "Total Spend",
        value: `₹${Number(
          data.totalSpend || 0
        ).toLocaleString("en-IN")}`,
        caption: "Lifetime purchases",
        icon: <WalletIcon />,
      },
      {
        label: "Pending Orders",
        value: data.pendingOrders,
        caption: "Awaiting completion",
        icon: <ClockIcon />,
      },
    ],
    [data]
  );

  /* ============================================================
     FETCH DATA
     ============================================================ */

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = () => {
      const token =
        localStorage.getItem("token");

      if (!token) return;

      $.ajax({
        url: `${ApiURl}/getUser.php`,
        method: "GET",
        data: { token },
      })
        .done((response) => {
          if (!isMounted) return;

          if (response?.success) {
            setUserData(
              response.ver_user || {}
            );

            return;
          }

          clearAuthState();
        })
        .fail((err) => {
          console.error(
            "Error fetching user data:",
            err
          );

          if (isMounted) {
            clearAuthState();
          }
        });
    };

    const fetchDashboardData =
      async () => {
        try {
          setLoading(true);
          setError(null);

          const userId =
            localStorage.getItem(
              "user_id"
            );

          if (!userId) {
            setError(
              "User not logged in."
            );

            return;
          }

          const response = await fetch(
            `${ApiURl}/getUserDataDashboard.php?user_id=${encodeURIComponent(
              userId
            )}`
          );

          const result =
            await response.json();

          if (!isMounted) return;

          if (result?.success) {
            const d =
              result.data || {};

            const recent =
              Array.isArray(
                d.recentOrders
              )
                ? d.recentOrders
                : [];

            setData({
              totalOrders: Number(
                d.totalOrders || 0
              ),

              totalSpend: Number(
                d.totalSpend || 0
              ),

              pendingOrders: Number(
                d.pendingOrders || 0
              ),

              recentOrders: recent,
            });

            setPage(1);
          } else {
            setError(
              result?.message ||
                "Failed to fetch data."
            );
          }
        } catch (err) {
          console.error(err);

          if (isMounted) {
            setError(
              "An error occurred while fetching data."
            );
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

    fetchUserData();
    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  /* ============================================================
     PAGINATION
     ============================================================ */

  const recentOrders =
    data.recentOrders || [];

  const totalPages = Math.max(
    1,
    Math.ceil(
      recentOrders.length / pageSize
    )
  );

  const safePage = Math.min(
    page,
    totalPages
  );

  const start =
    (safePage - 1) * pageSize;

  const pagedRecent =
    recentOrders.slice(
      start,
      start + pageSize
    );

  const goToPage = (p) => {
    setPage(
      Math.min(
        Math.max(1, p),
        totalPages
      )
    );
  };

  /* ============================================================
     GSAP
     ============================================================ */

  useEffect(() => {
    if (
      !containerRef.current ||
      loading
    ) {
      return;
    }

    const prefersReducedMotion =
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        welcomeRef.current,
        {
          y: 12,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.55,
          ease: "power2.out",
        }
      );

      gsap.fromTo(
        ".stat-card",
        {
          y: 10,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.45,
          ease: "power2.out",
          stagger: 0.07,
          delay: 0.06,
        }
      );

      gsap.fromTo(
        tableRef.current,
        {
          y: 10,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          ease: "power2.out",
          delay: 0.1,
        }
      );

      gsap.fromTo(
        ".recent-row",
        {
          y: 8,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          ease: "power2.out",
          stagger: 0.05,
          delay: 0.12,
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, [
    loading,
    safePage,
    recentOrders.length,
  ]);

  /* ============================================================
     PAGINATION COMPONENT
     ============================================================ */

  const Pagination = () => {
    if (
      recentOrders.length <=
      pageSize
    ) {
      return null;
    }

    const canPrev = safePage > 1;
    const canNext =
      safePage < totalPages;

    return (
      <div
        className="
          mt-5
          flex
          flex-col
          items-center
          justify-between
          gap-3

          sm:flex-row
        "
      >
        <p
          className="
            text-[9px]
            uppercase
            tracking-[.1em]

            sm:text-[10px]
          "
          style={{
            color: TEXT_LIGHT,
          }}
        >
          Showing{" "}
          <span
            className="font-semibold"
            style={{
              color: MAROON,
            }}
          >
            {recentOrders.length === 0
              ? 0
              : start + 1}
            –
            {Math.min(
              start + pageSize,
              recentOrders.length
            )}
          </span>{" "}
          of{" "}
          <span
            className="font-semibold"
            style={{
              color: MAROON,
            }}
          >
            {recentOrders.length}
          </span>
        </p>

        <div
          className="
            flex
            items-center
            gap-2
          "
        >
          <button
            type="button"
            onClick={() =>
              goToPage(safePage - 1)
            }
            disabled={!canPrev}
            className="
              rounded-full
              border
              px-4
              py-2

              text-[9px]
              font-semibold
              uppercase
              tracking-[.1em]

              disabled:cursor-not-allowed
              disabled:opacity-35
            "
            style={{
              color: MAROON,
              borderColor:
                GOLD_BORDER,
              backgroundColor: CREAM,
            }}
          >
            Prev
          </button>

          <div
            className="
              rounded-full
              border
              px-4
              py-2

              text-[9px]
              font-semibold
            "
            style={{
              color: GOLD,
              backgroundColor: MAROON,
              borderColor: MAROON,
            }}
          >
            {safePage} / {totalPages}
          </div>

          <button
            type="button"
            onClick={() =>
              goToPage(safePage + 1)
            }
            disabled={!canNext}
            className="
              rounded-full
              border
              px-4
              py-2

              text-[9px]
              font-semibold
              uppercase
              tracking-[.1em]

              disabled:cursor-not-allowed
              disabled:opacity-35
            "
            style={{
              color: MAROON,
              borderColor:
                GOLD_BORDER,
              backgroundColor: CREAM,
            }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {
    return (
      <section
        className="
          min-h-screen
          w-full

          px-3
          py-10

          sm:px-6
          sm:py-14

          lg:px-10
          lg:py-16
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            mx-auto
            max-w-[1100px]
            animate-pulse
          "
        >

          {/* HEADING */}

          <div className="text-center">

            <div
              className="
                mx-auto
                h-3
                w-24
                rounded-full
              "
              style={{
                backgroundColor:
                  "rgba(43,0,0,.08)",
              }}
            />

            <div
              className="
                mx-auto
                mt-4
                h-9
                w-64
                max-w-full
                rounded
              "
              style={{
                backgroundColor:
                  "rgba(43,0,0,.10)",
              }}
            />

            <div
              className="
                mx-auto
                mt-3
                h-3
                w-72
                max-w-full
                rounded
              "
              style={{
                backgroundColor:
                  "rgba(43,0,0,.06)",
              }}
            />

          </div>

          {/* STATS */}

          <div
            className="
              mt-8
              grid
              grid-cols-3
              gap-2

              sm:gap-3
            "
          >
            {[1, 2, 3].map(
              (item) => (
                <div
                  key={item}
                  className="
                    h-[100px]
                    rounded-[16px]

                    sm:h-[120px]
                  "
                  style={{
                    backgroundColor:
                      CREAM,
                  }}
                />
              )
            )}
          </div>

          {/* ORDERS */}

          <div
            className="
              mt-4
              overflow-hidden
              rounded-[20px]
              border
            "
            style={{
              backgroundColor: CREAM,
              borderColor:
                GOLD_BORDER,
            }}
          >
            <div
              className="
                border-b
                p-5
              "
              style={{
                borderColor: BORDER,
              }}
            >
              <div
                className="
                  h-6
                  w-40
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.10)",
                }}
              />

              <div
                className="
                  mt-2
                  h-3
                  w-56
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.06)",
                }}
              />
            </div>

            <div
              className="
                space-y-2
                p-4
              "
            >
              {[1, 2, 3, 4, 5].map(
                (item) => (
                  <div
                    key={item}
                    className="
                      h-14
                      rounded-[12px]
                    "
                    style={{
                      backgroundColor:
                        SOFT_CREAM,
                    }}
                  />
                )
              )}
            </div>
          </div>

        </div>
      </section>
    );
  }

  /* ============================================================
     ERROR
     ============================================================ */

  if (error) {
    return (
      <section
        className="
          flex
          min-h-[70vh]
          items-center
          justify-center

          px-4
          py-12
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            w-full
            max-w-[500px]

            rounded-[20px]
            border

            p-7
            text-center
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              mx-auto
              flex
              h-12
              w-12
              items-center
              justify-center

              rounded-full

              font-serif
              text-[21px]
            "
            style={{
              color: GOLD,
              backgroundColor: MAROON,
            }}
          >
            !
          </div>

          <h2
            className="
              mt-4
              font-serif
              text-[23px]
              font-medium
            "
            style={{
              color: MAROON,
            }}
          >
            Dashboard Unavailable
          </h2>

          <p
            className="
              mt-2
              text-[11px]
            "
            style={{
              color: TEXT_SOFT,
            }}
          >
            {error}
          </p>
        </div>
      </section>
    );
  }

  /* ============================================================
     MAIN
     ============================================================ */

  return (
    <section
      ref={containerRef}
      className="
        min-h-screen
        w-full

        px-3
        py-10

        sm:px-6
        sm:py-14

        lg:px-10
        lg:py-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      <div
        className="
          mx-auto
          max-w-[1100px]
        "
      >

        {/* ======================================================
            WELCOME
            ====================================================== */}

        <div
          ref={welcomeRef}
          className="
            mb-7
            text-center

            sm:mb-9
          "
        >

          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor:
                  MAROON,
                opacity: 0.48,
              }}
            />

            <span
              className="
                text-[8px]
                font-semibold
                uppercase
                tracking-[.28em]

                sm:text-[9px]
              "
              style={{
                color: MAROON,
              }}
            >
              SL Gold
            </span>

            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor:
                  MAROON,
                opacity: 0.48,
              }}
            />
          </div>

          <h1
            className="
              font-serif

              text-[31px]
              font-medium
              leading-[1.08]
              tracking-[-.025em]

              sm:text-[38px]

              lg:text-[44px]
            "
            style={{
              color: MAROON,
            }}
          >
            Hello,{" "}
            {userData?.firstname ||
              "User"}
          </h1>

          <p
            className="
              mx-auto
              mt-2
              max-w-[480px]

              font-serif
              text-[13px]

              sm:text-[15px]
            "
            style={{
              color: MAROON,
              opacity: 0.62,
            }}
          >
            Welcome back to your SL Gold
            account.
          </p>

        </div>

        {/* ======================================================
            STATS
            ====================================================== */}

        <div
          ref={statsRef}
          className="
            grid
            grid-cols-3
            gap-2

            sm:gap-3

            lg:gap-4
          "
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="
                stat-card

                relative
                overflow-hidden

                rounded-[14px]
                border

                px-2.5
                py-3

                sm:rounded-[17px]
                sm:p-4

                lg:p-5
              "
              style={{
                backgroundColor: CREAM,
                borderColor:
                  GOLD_BORDER,
              }}
            >

              {/* GOLD DETAIL */}

              <span
                className="
                  absolute
                  left-0
                  top-0

                  h-[2px]
                  w-full
                "
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(212,175,55,.55), transparent)",
                }}
              />

              <div
                className="
                  flex
                  flex-col
                  gap-2

                  sm:flex-row
                  sm:items-start
                  sm:justify-between
                  sm:gap-3
                "
              >

                <div
                  className="
                    min-w-0
                    flex-1
                  "
                >
                  <p
                    className="
                      text-[7px]
                      font-semibold
                      uppercase
                      tracking-[.09em]

                      sm:text-[9px]
                      sm:tracking-[.13em]
                    "
                    style={{
                      color:
                        TEXT_LIGHT,
                    }}
                  >
                    {stat.label}
                  </p>

                  <p
                    className="
                      mt-1
                      break-words
                      font-serif

                      text-[18px]
                      font-semibold
                      leading-tight

                      sm:mt-2
                      sm:text-[24px]

                      lg:text-[29px]
                    "
                    style={{
                      color: MAROON,
                    }}
                  >
                    {stat.value}
                  </p>

                  <p
                    className="
                      mt-1
                      hidden
                      text-[9px]

                      sm:block
                    "
                    style={{
                      color:
                        TEXT_LIGHT,
                    }}
                  >
                    {stat.caption}
                  </p>
                </div>

                <div
                  className="
                    flex
                    h-7
                    w-7
                    shrink-0
                    items-center
                    justify-center

                    rounded-full

                    sm:h-9
                    sm:w-9
                  "
                  style={{
                    color: GOLD,
                    backgroundColor:
                      MAROON,
                  }}
                >
                  {React.cloneElement(
                    stat.icon,
                    {
                      size: 14,
                    }
                  )}
                </div>

              </div>

            </div>
          ))}
        </div>

        {/* ======================================================
            RECENT ORDERS
            ====================================================== */}

        <section
          ref={tableRef}
          className="
            mt-4
            overflow-hidden

            rounded-[20px]
            border

            sm:mt-5
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
            boxShadow:
              "0 14px 40px rgba(43,0,0,.04)",
          }}
        >

          {/* HEADER */}

          <div
            className="
              flex
              items-end
              justify-between
              gap-4

              border-b

              px-4
              py-4

              sm:px-6
              sm:py-5
            "
            style={{
              borderColor: BORDER,
            }}
          >

            <div>

              <p
                className="
                  text-[8px]
                  font-semibold
                  uppercase
                  tracking-[.2em]
                "
                style={{
                  color: GOLD,
                }}
              >
                Purchases
              </p>

              <h2
                className="
                  mt-1
                  font-serif

                  text-[20px]
                  font-medium

                  sm:text-[23px]
                "
                style={{
                  color: MAROON,
                }}
              >
                Recent Orders
              </h2>

              <p
                className="
                  mt-1
                  text-[9px]

                  sm:text-[10px]
                "
                style={{
                  color: TEXT_LIGHT,
                }}
              >
                Your latest purchases and
                receipts.
              </p>

            </div>

            <div
              className="
                hidden
                h-9
                w-9
                shrink-0
                items-center
                justify-center

                rounded-full

                sm:flex
              "
              style={{
                color: GOLD,
                backgroundColor: MAROON,
              }}
            >
              <ReceiptIcon size={15} />
            </div>

          </div>

          <div
            className="
              p-3

              sm:p-5

              lg:p-6
            "
          >
            {recentOrders.length > 0 ? (
              <>

                {/* ==============================================
                    DESKTOP TABLE
                    ============================================== */}

                <div
                  className="
                    hidden
                    overflow-hidden
                    rounded-[14px]
                    border

                    sm:block
                  "
                  style={{
                    borderColor: BORDER,
                  }}
                >
                  <table className="w-full">

                    <thead>
                      <tr
                        style={{
                          backgroundColor:
                            MAROON,
                        }}
                      >
                        <TableHead>
                          Order
                        </TableHead>

                        <TableHead>
                          Date
                        </TableHead>

                        <TableHead>
                          Amount
                        </TableHead>

                        <TableHead right>
                          Action
                        </TableHead>
                      </tr>
                    </thead>

                    <tbody>
                      {pagedRecent.map(
                        (order) => (
                          <tr
                            key={
                              order.order_id
                            }
                            className="
                              recent-row
                              border-t
                            "
                            style={{
                              borderColor:
                                BORDER,
                            }}
                          >
                            <td
                              className="
                                px-4
                                py-3.5

                                font-serif
                                text-[13px]
                                font-semibold
                              "
                              style={{
                                color:
                                  MAROON,
                              }}
                            >
                              #
                              {
                                order.order_id
                              }
                            </td>

                            <td
                              className="
                                px-4
                                py-3.5

                                text-[10px]
                              "
                              style={{
                                color:
                                  TEXT_SOFT,
                              }}
                            >
                              {order.date}
                            </td>

                            <td
                              className="
                                px-4
                                py-3.5

                                font-serif
                                text-[14px]
                                font-semibold
                              "
                              style={{
                                color:
                                  MAROON,
                              }}
                            >
                              ₹
                              {
                                order.amount
                              }
                            </td>

                            <td
                              className="
                                px-4
                                py-3.5
                                text-right
                              "
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/Receipt/${order.order_id}`
                                  )
                                }
                                className="
                                  inline-flex
                                  items-center
                                  gap-2

                                  rounded-full

                                  px-4
                                  py-2

                                  text-[8px]
                                  font-semibold
                                  uppercase
                                  tracking-[.1em]
                                "
                                style={{
                                  color:
                                    GOLD,
                                  backgroundColor:
                                    MAROON,
                                }}
                              >
                                Receipt

                                <ArrowIcon />
                              </button>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>

                  </table>
                </div>

                {/* ==============================================
                    MOBILE CARDS
                    ============================================== */}

                <div
                  className="
                    space-y-2

                    sm:hidden
                  "
                >
                  {pagedRecent.map(
                    (order) => (
                      <article
                        key={
                          order.order_id
                        }
                        className="
                          recent-row

                          rounded-[13px]
                          border

                          p-3.5
                        "
                        style={{
                          backgroundColor:
                            SOFT_CREAM,
                          borderColor:
                            BORDER,
                        }}
                      >

                        <div
                          className="
                            flex
                            items-start
                            justify-between
                            gap-3
                          "
                        >

                          <div>

                            <p
                              className="
                                text-[7px]
                                font-semibold
                                uppercase
                                tracking-[.14em]
                              "
                              style={{
                                color:
                                  GOLD,
                              }}
                            >
                              Order
                            </p>

                            <p
                              className="
                                mt-0.5
                                font-serif

                                text-[16px]
                                font-semibold
                              "
                              style={{
                                color:
                                  MAROON,
                              }}
                            >
                              #
                              {
                                order.order_id
                              }
                            </p>

                          </div>

                          <p
                            className="
                              font-serif
                              text-[17px]
                              font-semibold
                            "
                            style={{
                              color:
                                MAROON,
                            }}
                          >
                            ₹{order.amount}
                          </p>

                        </div>

                        <div
                          className="
                            mt-3
                            flex
                            items-center
                            justify-between
                            gap-3

                            border-t
                            pt-3
                          "
                          style={{
                            borderColor:
                              BORDER,
                          }}
                        >

                          <p
                            className="
                              min-w-0
                              text-[9px]
                            "
                            style={{
                              color:
                                TEXT_LIGHT,
                            }}
                          >
                            {order.date}
                          </p>

                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/Receipt/${order.order_id}`
                              )
                            }
                            className="
                              inline-flex
                              shrink-0
                              items-center
                              gap-1.5

                              rounded-full

                              px-3
                              py-2

                              text-[7px]
                              font-semibold
                              uppercase
                              tracking-[.09em]
                            "
                            style={{
                              color: GOLD,
                              backgroundColor:
                                MAROON,
                            }}
                          >
                            Receipt

                            <ArrowIcon
                              size={10}
                            />
                          </button>

                        </div>

                      </article>
                    )
                  )}
                </div>

                {/* PAGINATION */}

                <Pagination />

                {/* VIEW ALL */}

                <div
                  className="
                    mt-5
                    flex
                    justify-center

                    sm:mt-6
                  "
                >
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        "/order-section"
                      )
                    }
                    className="
                      inline-flex
                      items-center
                      justify-center
                      gap-2

                      rounded-full

                      px-5
                      py-2.5

                      text-[8px]
                      font-semibold
                      uppercase
                      tracking-[.12em]

                      sm:px-6
                      sm:py-3
                      sm:text-[9px]
                    "
                    style={{
                      color: GOLD,
                      backgroundColor:
                        MAROON,
                    }}
                  >
                    View All Orders

                    <ArrowIcon />
                  </button>
                </div>

              </>
            ) : (
              /* ================================================
                 EMPTY
                 ================================================ */

              <div
                className="
                  rounded-[14px]
                  border

                  px-4
                  py-8

                  text-center
                "
                style={{
                  backgroundColor:
                    SOFT_CREAM,
                  borderColor: BORDER,
                }}
              >
                <div
                  className="
                    mx-auto
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center

                    rounded-full
                  "
                  style={{
                    color: GOLD,
                    backgroundColor:
                      MAROON,
                  }}
                >
                  <BagIcon size={15} />
                </div>

                <h3
                  className="
                    mt-3
                    font-serif
                    text-[18px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  No Orders Yet
                </h3>

                <p
                  className="
                    mt-1
                    text-[9px]
                  "
                  style={{
                    color: TEXT_LIGHT,
                  }}
                >
                  Your recent purchases will
                  appear here.
                </p>
              </div>
            )}
          </div>

          {/* GOLD BOTTOM DETAIL */}

          <div
            className="
              h-[2px]
              w-full
            "
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,175,55,.70), transparent)",
            }}
          />

        </section>

      </div>
    </section>
  );
};

/* ============================================================
   TABLE HEADER
   ============================================================ */

const TableHead = ({
  children,
  right = false,
}) => (
  <th
    className={`
      px-4
      py-3

      text-[8px]
      font-semibold
      uppercase
      tracking-[.14em]

      ${
        right
          ? "text-right"
          : "text-left"
      }
    `}
    style={{
      color: GOLD,
    }}
  >
    {children}
  </th>
);

export default DashboardSection;